export const RETREAT_IMAGE_ACCEPT =
  ".jpg,.jpeg,.png,.webp,.heic,.heif,image/jpeg,image/png,image/webp,image/heic,image/heif";

export const RETREAT_IMAGE_MAX_WIDTH = 1920;
export const RETREAT_IMAGE_MAX_HEIGHT = 1620;
export const RETREAT_IMAGE_MAX_FILE_SIZE_MB = 3;

const ALLOWED_MIME_TYPES = new Set([
  "image/jpeg",
  "image/jpg",
  "image/png",
  "image/webp",
  "image/heic",
  "image/heif",
]);

const ALLOWED_EXTENSIONS = new Set([
  "jpg",
  "jpeg",
  "png",
  "webp",
  "heic",
  "heif",
]);

const WEB_READY_EXTENSIONS = new Set(["jpg", "jpeg", "png", "webp"]);

export interface ScaledDimensions {
  width: number;
  height: number;
}

function getFileExtension(fileName: string): string {
  const parts = fileName.split(".");
  return parts.length > 1 ? parts.pop()!.toLowerCase() : "";
}

function isAllowedRetreatImageFile(file: File): boolean {
  const ext = getFileExtension(file.name);
  if (ext === "pdf") {
    return false;
  }
  if (ALLOWED_EXTENSIONS.has(ext)) {
    return true;
  }
  const mime = (file.type || "").toLowerCase();
  return ALLOWED_MIME_TYPES.has(mime);
}

export function getRetreatImageScaledDimensions(
  width: number,
  height: number,
): ScaledDimensions {
  if (width <= RETREAT_IMAGE_MAX_WIDTH && height <= RETREAT_IMAGE_MAX_HEIGHT) {
    return { width, height };
  }

  const scale = Math.min(
    RETREAT_IMAGE_MAX_WIDTH / width,
    RETREAT_IMAGE_MAX_HEIGHT / height,
  );

  return {
    width: Math.round(width * scale),
    height: Math.round(height * scale),
  };
}

function loadImageFromFile(file: File): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    const objectUrl = URL.createObjectURL(file);

    img.onload = () => {
      URL.revokeObjectURL(objectUrl);
      resolve(img);
    };

    img.onerror = () => {
      URL.revokeObjectURL(objectUrl);
      reject(
        new Error(
          "Unable to read this image. Please try JPG, PNG, or WebP. HEIC is only supported in some browsers.",
        ),
      );
    };

    img.src = objectUrl;
  });
}

function canvasToFile(
  canvas: HTMLCanvasElement,
  fileName: string,
  mimeType: string,
  quality?: number,
): Promise<File> {
  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => {
        if (!blob) {
          reject(new Error("Failed to process image."));
          return;
        }

        const baseName = fileName.replace(/\.[^.]+$/, "") || "retreat-image";
        const extension =
          mimeType === "image/png"
            ? "png"
            : mimeType === "image/webp"
              ? "webp"
              : "jpg";

        resolve(
          new File([blob], `${baseName}.${extension}`, {
            type: mimeType,
            lastModified: Date.now(),
          }),
        );
      },
      mimeType,
      quality,
    );
  });
}

function shouldUsePngOutput(file: File): boolean {
  const ext = getFileExtension(file.name);
  return ext === "png" || file.type === "image/png";
}

export function resolveRetreatImageUrl(
  image?: string | null,
): string {
  if (!image) return "";
  if (
    image.startsWith("blob:") ||
    image.startsWith("data:") ||
    /^https?:\/\//i.test(image)
  ) {
    return image;
  }
  const api = process.env.REACT_APP_API_URL || "";
  const origin = api.replace(/\/api\/?$/, "").replace(/\/$/, "");
  if (image.startsWith("/public/")) return `${origin}${image}`;
  if (image.startsWith("/uploads/")) return `${origin}/public${image}`;
  if (image.startsWith("/")) return `${origin}/public${image}`;
  return `${origin}/public/retreatImage/${image}`;
}

export function getRetreatImagePreviewSrc(
  image: File | Blob | string | null | undefined,
): string {
  if (!image) {
    return "";
  }

  if (image instanceof File || image instanceof Blob) {
    return URL.createObjectURL(image);
  }

  if (typeof image === "string") {
    return resolveRetreatImageUrl(image);
  }

  return "";
}

export async function processRetreatImage(file: File | null | undefined): Promise<File> {
  if (!file) {
    throw new Error("No file selected.");
  }

  const fileSizeInMB = file.size / (1024 * 1024);
  if (fileSizeInMB > RETREAT_IMAGE_MAX_FILE_SIZE_MB) {
    throw new Error(
      `File size should be less than ${RETREAT_IMAGE_MAX_FILE_SIZE_MB} MB`,
    );
  }

  if (!isAllowedRetreatImageFile(file)) {
    throw new Error(
      "Please upload a JPG, JPEG, PNG, WebP, or HEIC image. PDF files are not accepted.",
    );
  }

  const img = await loadImageFromFile(file);
  const sourceWidth = img.naturalWidth || img.width;
  const sourceHeight = img.naturalHeight || img.height;
  const { width: targetWidth, height: targetHeight } =
    getRetreatImageScaledDimensions(sourceWidth, sourceHeight);

  const needsResize =
    sourceWidth !== targetWidth || sourceHeight !== targetHeight;
  const ext = getFileExtension(file.name);
  const isAlreadyWebReady =
    !needsResize && WEB_READY_EXTENSIONS.has(ext);

  if (isAlreadyWebReady) {
    return file;
  }

  const canvas = document.createElement("canvas");
  canvas.width = targetWidth;
  canvas.height = targetHeight;

  const ctx = canvas.getContext("2d");
  ctx.drawImage(img, 0, 0, targetWidth, targetHeight);

  const usePng = shouldUsePngOutput(file);
  const mimeType = usePng ? "image/png" : "image/jpeg";
  const quality = usePng ? undefined : 0.85;

  return canvasToFile(canvas, file.name, mimeType, quality);
}
